import { BucketType, EnvironmentMode, BrowserStorageKey, FileOfBucket, EFileExtensionUpload } from '../types';
import { AmplifyS3Config } from '../custom/type';
import { buildAmplifyS3Config } from '../utils/config.util';

export const MODE = EnvironmentMode.DEV;

export const isDevelopMode = (): boolean => {
    return MODE.toString() === EnvironmentMode.DEV.toString();
};

const MODE_DEV_PREFIX = 'dev-';
const BE_DEV_REGION = 'ap-southeast-2';
const BE_MASTER_REGION = 'ap-northeast-1';
const S3_SQLITE_BUCKET = 'aims-sqlite';
const S3_TIMESTREAM_BUCKET = 'aims-txt-timestream';
const S3_BLOCKING_BUCKET = 'aims-client-blocking';

export const COMPLETED_PERCENTAGE_NUM = 100;
export const APP_SCANNING_DELAY_MILLISECOND = 500;
export const APP_INTERVAL_TIME_SECOND = 10;
export const FILE_REFRESH_TIME_SECOND = 1;
export const DEFAULT_FILE_IN_BUCKET_COUNT = 0;
export const DEFAULT_OBJECT_LENGTH = 0;
export const ZERO_NUMBER = 0;
export const CONVERSION_UNIT = 1024;
export const MIN_SIZE_DB3_IN_BYTES = 512;
export const MIN_SIZE_CSV_IN_BYTES = 1;
export const BYTE_TO_GIGABYTE = Math.pow(CONVERSION_UNIT, 3);
export const BYTE_TO_MEGABYTE = Math.pow(CONVERSION_UNIT, 2);
export const MAX_SIZE_DB3_IN_BYTES = 2 * BYTE_TO_GIGABYTE;
export const MAX_SIZE_CSV_IN_BYTES = 2 * BYTE_TO_MEGABYTE;
export const EMPTY_STRING = '';
export const SECOND_TO_MILLISECOND = 1000;
export const FIVE_HUNDRED_MB_IN_BYTES = 500 * CONVERSION_UNIT * CONVERSION_UNIT;
export const ONE_GB_IN_BYTES = CONVERSION_UNIT * CONVERSION_UNIT * CONVERSION_UNIT;
export const NOP_THREE_MINUTES_MS = 3 * 60 * 1000;
export const NOP_FIVE_MINUTES_MS = 5 * 60 * 1000;
export const S3_CHECK_INTERVAL_MS = 1000;
export const VALID_LOG_FOLDERS = ['TraceLog', 'WaferLog', 'AlarmLog', 'SvLog'];
export const APP_TIMESTREAM_FOLDER = ['SvLog'];
export const APP_ACCEPT_EXTENSION_FILES_DB3 = ['.db3', '.xml'];
export const APP_ACCEPT_EXTENSION_FILES_CSV = ['.csv', '.json'];
export const FILENAME_BLOCKING_FLAG = 'blocking-ui.flag';
export const CHARACTER_TIMEZONE_GMT = 'GMT';
export const CHARACTER_SLASH = '/';
export const STRING_REGION = 'region';
export const CHARACTER_PERCENTAGE = '%';
export const CHARACTER_APPROXIMATION = '~';
export const NOT_FOUND_INDEX = -1;
export const REGEX_CAMEL_CASE = /(?=[A-Z])/;
export const DELETE_MODE_All = '0';
export const DELETE_MODE_AFTER = '1';
export const DELETE_MODE_BEFORE = '2';
export const FORMAT_DATE_TIME = 'YYYY-MM-DDTHH:mm:ss';
export const DATE_TIME_PICKER_FORMAT = 'YYYY-MM-DD HH:mm:ss';
export const TEXT_FIELD_VARIANT = 'standard';
export const CURSOR_POINTER = 'pointer';

const S3_SQLITE_BUCKET_DEV_MODE = MODE_DEV_PREFIX + S3_SQLITE_BUCKET;
const S3_BLOCKING_BUCKET_DEV_MODE = MODE_DEV_PREFIX + S3_BLOCKING_BUCKET;
const S3_TIMESTREAM_BUCKET_DEV_MODE = MODE_DEV_PREFIX + S3_TIMESTREAM_BUCKET;

export const S3_REGION = isDevelopMode() ? BE_DEV_REGION : BE_MASTER_REGION;
export const APP_SQLITE_BUCKET = isDevelopMode() ? S3_SQLITE_BUCKET_DEV_MODE : S3_SQLITE_BUCKET;
export const APP_BLOCKING_BUCKET = isDevelopMode() ? S3_BLOCKING_BUCKET_DEV_MODE : S3_BLOCKING_BUCKET;
export const APP_TIMESTREAM_BUCKET = isDevelopMode() ? S3_TIMESTREAM_BUCKET_DEV_MODE : S3_TIMESTREAM_BUCKET;

export const APP_SQLITE_CONFIG: AmplifyS3Config<BucketType> = buildAmplifyS3Config(
    BucketType.SQLITE,
    APP_SQLITE_BUCKET,
    S3_REGION,
    BrowserStorageKey.S3_BUCKET_SQLITE,
);

export const APP_BLOCKING_CONFIG: AmplifyS3Config<BucketType> = buildAmplifyS3Config(
    BucketType.BLOCKING,
    APP_BLOCKING_BUCKET,
    S3_REGION,
    BrowserStorageKey.S3_BUCKET_CLIENT_BLOCKING,
);

export const APP_TIMESTREAM_CONFIG: AmplifyS3Config<BucketType> = buildAmplifyS3Config(
    BucketType.TIMESTREAM,
    APP_TIMESTREAM_BUCKET,
    S3_REGION,
    BrowserStorageKey.S3_BUCKET_TIMESTREAM,
);

export const ALL_S3_CONFIGS = [APP_SQLITE_CONFIG, APP_BLOCKING_CONFIG, APP_TIMESTREAM_CONFIG];
export const ALL_S3_BUCKETS = [S3_SQLITE_BUCKET, S3_BLOCKING_BUCKET, S3_TIMESTREAM_BUCKET];
export const HTTP_STATUS_CODE_NOT_FOUND = 404;
export const REGEX_SNAKE_CASE = /([-_][a-z])/g;
export const REGEX_UPPER_CASE_FIRST_LETTER = /\b\w/g;
export const REGEX_ISO_DATE = /\.\d{3}Z$/;
export const CHARACTER_ISO_DATE_SUFFIX = 'Z';
export const CHARACTER_MINUS = '-';
export const CHARACTER_UNDERLINE = '_';
export const VARIABLE_TYPE_OBJECT = 'object';

export const FileSupport = {
    [EFileExtensionUpload.DB3]: {
        getMaxSizeInBytes: (): number => MAX_SIZE_DB3_IN_BYTES,
        getMinSizeInBytes: (): number => MIN_SIZE_DB3_IN_BYTES,
    },
    [EFileExtensionUpload.CSV]: {
        getMaxSizeInBytes: (): number => MAX_SIZE_CSV_IN_BYTES,
        getMinSizeInBytes: (): number => MIN_SIZE_CSV_IN_BYTES,
    },
};
// Save the reference to FileSupport object on the Window object
if (window.Cypress) {
    window.FileSupport = FileSupport;
}

export const PATH_INDEX = {
    CSV: {
        LOG_FOLDER: 1,
        TARGET_FOLDER: 2,
        FILEROOT_LEVEL: 3,
        STRICT_LEVEL: 4,
        EQUIPMENT_ID_LEVEL: 1,
        KEY_LEVEL: 2,
    },
    DB3: {
        LOG_FOLDER: 2,
        TARGET_FOLDER: 3,
        FILEROOT_LEVEL: 3,
        STRICT_LEVEL: 5,
        EQUIPMENT_ID_LEVEL: 0,
        KEY_LEVEL: 1,
    },
} as const;

export const FILE_TABLE_COLUMN_KEY = {
    FILE: 'file',
    FOLDER: 'folder',
    SIZE: 'size',
    PROGRESS: 'progress',
    EQUIPMENT_ID: 'equipmentId',
    DATE: 'date',
};

export const DEFAULT_BUCKET_INFORMATION: FileOfBucket = {
    name: S3_SQLITE_BUCKET_DEV_MODE,
    totalFile: ZERO_NUMBER,
    totalSize: ZERO_NUMBER,
    progress: ZERO_NUMBER,
    objects: [],
    isError: false,
};

export const AUTH_EVENT_TYPE = {
    SIGN_IN_WITH_REDIRECT: `signInWithRedirect`,
    SIGN_IN_WITH_REDIRECT_FAILURE: `signInWithRedirect_failure`,
    TOKEN_REFRESH: `tokenRefresh`,
    TOKEN_REFRESH_FAILURE: `tokenRefresh_failure`,
    CUSTOM_OAUTH_STATE: `customOAuthState`,
    SIGNED_IN: `signedIn`,
    SIGNED_OUT: `signedOut`,
};
